/**
 * WixMart — спільне джерело даних для ПРАВИЛ кошика. Дає дві речі:
 *
 *   1. frozen()            → список товарів колекції mrozonki (термобокс 19 zł).
 *      Заморозка визначається КОЛЕКЦІЄЮ, бо мітки для неї не існує, а колекція
 *      має ручне сортування (sortOrder=MANUAL) — переводити її на мітку
 *      означало б завести друге місце правди і рано чи пізно розійтися з ним
 *      (= заморозка без термобокса). Колекція лишається єдиним джерелом.
 *
 *   2. productTags(handle) → мітки товару (кауція + алкоголь 18+).
 *      Алкоголь визначається МІТКОЮ `alkohol-18`, а не колекцією — так само, як
 *      це вже робить вікова заглушка (snippets/wixmart-age-gate.liquid).
 *      Перевірено 15.07.2026: мітка стоїть на 252 товарах, колекція має 244,
 *      товарів у колекції без мітки — 0. Тобто мітка покриває колекцію
 *      повністю, а ще ловить 8 активних пив, які в колекцію не додані і через
 *      це РАНІШЕ йшли в кошик без плашки «кур'єр перевірить документ».
 *      Виняток `bez-18` (безалкогольне 0,0%) — як у віковій заглушці.
 *
 * ЧОМУ ОКРЕМИЙ ФАЙЛ (2026-07-15):
 * раніше ці списки тягнули незалежно `wxm-kaucja-tandem.js` і
 * `wixmart-cart-enhancements.liquid` — кожен зі своїм кешем. Наслідок:
 * /collections/mrozonki/products.json вантажився ДВІЧІ на кожній сторінці
 * (2×40 KB) плюс alkohol (147 KB), і навіть коли кошик порожній.
 *
 * ⚠️ ДВА ПРАВИЛА, ЯКІ НЕ МОЖНА ЛАМАТИ — обидва вже коштували грошей:
 *
 * 1. ПРОВАЛ ЗАПИТУ НЕ КЕШУЄМО І НЕ ВИДАЄМО ЗА ПОРОЖНІЙ СПИСОК.
 *    Було: `.catch(() => ({}))` — помилка кешувала ПОРОЖНІЙ список на весь час
 *    життя сторінки. Порожній список = «заморозки в кошику нема» = термобокс
 *    не додається, а вже доданий — ВИДАЛЯЄТЬСЯ. Один невдалий запит (напр. 429
 *    від Shopify при штормі запитів) = замовлення із заморозкою без термобокса.
 *    Тому помилка → `null` («не знаю») + наступний виклик пробує ще раз.
 *    null НІКОЛИ не можна трактувати як «немає заморозки/алкоголю».
 *
 * 2. СТОРІНКУЄМО. `?limit=250` — стеля Shopify за один запит. Станом на
 *    15.07.2026 в alkohol 218 товарів — ще 32, і частина алкоголю мовчки
 *    лишилась би без плашки 18+. Тому читаємо сторінками до кінця.
 */
(function () {
  'use strict';

  var PAGE_LIMIT = 250;
  var MAX_PAGES = 12; // запобіжник від нескінченного циклу (=3000 товарів)
  var SS_TTL_MS = 60 * 60 * 1000; // 1 год — списки колекцій рідко міняються за візит

  var cache = {}; // handle колекції → Promise<{handle:true} | null>

  // sessionStorage-кеш списків колекцій. Кошик персистентний: клієнт з пивом
  // у кошику гортає каталог, і render() на КОЖНІЙ сторінці інакше тягнув би
  // список колекції alkohol (147 KB). Кеш на вкладку прибирає це повторення.
  function ssRead(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || typeof obj.t !== 'number') return null;
      if (Date.now() - obj.t > SS_TTL_MS) { sessionStorage.removeItem(key); return null; }
      return obj.set || null;
    } catch (e) { return null; }
  }
  function ssWrite(key, set) {
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), set: set })); } catch (e) {}
  }

  function fetchPage(collectionHandle, page) {
    var url = '/collections/' + collectionHandle + '/products.json?limit=' + PAGE_LIMIT + '&page=' + page;
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' на ' + url);
      return r.json();
    });
  }

  // ⛔ Легкий шлях: `products.json` віддає ПОВНІ картки — 1 095 972 байти на
  // 241 товар (заміряно 27.08.2026), і все це заради перевірки однієї позиції
  // кошика. Шаблон `wxm-handles` віддає самі ручки, ~30 байт на товар.
  // Сторінка тут менша (50 проти 250), тож запитів більше — але кожен важить
  // менше за 2 КБ замість 198 КБ.
  var LIGHT_PAGE = 50;

  function fetchHandlesPage(collectionHandle, page) {
    var url = '/collections/' + collectionHandle + '?view=wxm-handles&page=' + page;
    return fetch(url, { headers: { Accept: 'text/plain' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status + ' на ' + url);
      return r.text();
    });
  }

  function loadLight(collectionHandle) {
    var set = {};
    var seen = 0;

    function step(page) {
      return fetchHandlesPage(collectionHandle, page).then(function (text) {
        var lines = String(text).split('\n');
        var more = false;
        for (var i = 0; i < lines.length; i++) {
          var h = lines[i].trim();
          if (!h) continue;
          if (h === 'END') { more = false; continue; }
          if (h === 'MORE') { more = true; continue; }
          set[h] = true;
          seen++;
        }
        // Ані MORE, ані END — це не наш шаблон, а звичайна сторінка колекції.
        // Краще чесно впасти й піти запасним шляхом, ніж віддати покришений
        // список: на ньому тримається плашка 18+.
        if (!seen && !more) throw new Error('шаблон wxm-handles не відповів переліком');
        if (more && page < MAX_PAGES) return step(page + 1);
        return set;
      });
    }

    return step(1);
  }

  function loadAll(collectionHandle) {
    var set = {};

    function step(page) {
      return fetchPage(collectionHandle, page).then(function (data) {
        var products = (data && data.products) || [];
        products.forEach(function (p) {
          set[p.handle] = true;
        });
        // Менше за повну сторінку — це кінець колекції.
        if (products.length < PAGE_LIMIT || page >= MAX_PAGES) return set;
        return step(page + 1);
      });
    }

    return step(1);
  }

  /**
   * Повертає Promise зі словником { handle: true } або null, якщо список
   * завантажити не вдалося. null = «не знаю», а не «порожньо».
   */
  function load(collectionHandle) {
    if (cache[collectionHandle]) return cache[collectionHandle];

    var ssKey = 'wxm_coll_' + collectionHandle;
    var cached = ssRead(ssKey);
    if (cached) {
      cache[collectionHandle] = Promise.resolve(cached);
      return cache[collectionHandle];
    }

    // Спершу легкий шлях; якщо шаблон не відповів як треба — старий повний,
    // щоб плашка 18+ не зникла через нашу оптимізацію.
    cache[collectionHandle] = loadLight(collectionHandle).catch(function (e) {
      try {
        console.warn('[wxm-collections] легкий перелік «' + collectionHandle +
                     '» не вийшов, беру повний:', e);
      } catch (x) {}
      return loadAll(collectionHandle);
    }).then(function (set) {
      ssWrite(ssKey, set);
      return set;
    }).catch(function (e) {
      // Провал НЕ кешуємо — наступний виклик спробує знову.
      cache[collectionHandle] = null;
      try {
        console.warn('[wxm-collections] «' + collectionHandle + '» не завантажився:', e);
      } catch (x) {}
      return null;
    });

    return cache[collectionHandle];
  }

  // ── Мітки товару за handle ────────────────────────────────────────────────
  //
  // Спільний кеш на всю тему. Кауція (wxm-kaucja-tandem.js) читає звідси мітки
  // kaucja-pet-50gr / kaucja-puszka-50gr, а панелі кошика
  // (wixmart-cart-enhancements.liquid) — мітку alkohol-18. Отже на одну позицію
  // кошика — ОДИН запит /products/<handle>.js на обидва правила, а не два.
  //
  // Повертає масив міток або null, якщо прочитати не вдалося. null = «не знаю»,
  // НЕ «міток нема» — той самий принцип, що й для списків колекцій вище.
  var tagCache = {};

  function productTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (tagCache[handle]) return tagCache[handle];

    tagCache[handle] = fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (p) {
        return (p.tags || []).map(function (t) {
          return String(t).toLowerCase();
        });
      })
      .catch(function (e) {
        tagCache[handle] = null; // провал не кешуємо
        try {
          console.warn('[wxm-collections] мітки «' + handle + '» не прочитались:', e);
        } catch (x) {}
        return null;
      });

    return tagCache[handle];
  }

  window.wxmCartCollections = {
    frozen: function () {
      return load('mrozonki');
    },
    // Список товарів колекції alkohol — щоб у кошику відрізнити САМЕ алкоголь
    // (кур'єр + документ + InPost) від просто-18+ товару (енергетик). Тягнеться
    // лише коли в кошику вже є 18+ товар — тобто рідко. Кеш у sessionStorage.
    alcohol: function () {
      return load('alkohol');
    },
    productTags: productTags,
  };
})();
